import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { Media, ProductMediaItem } from '@nucleus-commerce/api-client/admin';

import { socialPostsApi } from '../api/social-posts-api';

import { socialKeys } from './social-keys';

export interface PostMediaCandidate {
  productId: string;
  media: Media;
  /** Variants the image is scoped to in the product gallery; empty means the whole product. */
  variantIds: string[];
}

export interface PostProductMedia {
  /** Every image of every chosen product, in product then gallery order, deduplicated by id. */
  candidates: PostMediaCandidate[];
  /** Media rows by id, for the strip and the preview. */
  lookup: ReadonlyMap<string, Media>;
  isPending: boolean;
  error: unknown;
}

/** The galleries of the chosen products, where the post's images are picked from. */
export function usePostProductMedia(productIds: readonly string[]): PostProductMedia {
  const combine = useCallback(
    (results: Array<UseQueryResult<ProductMediaItem[]>>): PostProductMedia => {
      const candidates: PostMediaCandidate[] = [];
      const lookup = new Map<string, Media>();
      results.forEach((result, index) => {
        const productId = productIds[index];
        if (!result.data || !productId) return;
        for (const item of [...result.data].sort((a, b) => a.position - b.position)) {
          if (item.media.kind !== 'image' || lookup.has(item.mediaId)) continue;
          lookup.set(item.mediaId, item.media);
          candidates.push({ productId, media: item.media, variantIds: item.variantIds });
        }
      });
      return {
        candidates,
        lookup,
        isPending: results.some((r) => r.isPending),
        error: results.find((r) => r.error)?.error,
      };
    },
    [productIds],
  );
  return useQueries({
    queries: productIds.map((productId) => ({
      queryKey: socialKeys.productMedia(productId),
      queryFn: () => socialPostsApi.productMedia(productId),
      staleTime: 60_000,
    })),
    combine,
  });
}
